// Global packages and components
import { clsx } from 'clsx'
import { ContentEditor, Accordion, Heading } from '~/components'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { useFaqs } from '~/hooks'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type { FaqItem, SanityFaqTaxonomyScheme } from '~/types'

interface FaqsBlockProps {
  content?: PortableTextBlock[]
  faqs?: FaqItem[] | null
  categories?: SanityFaqTaxonomyScheme['concepts'] | null
}

// Main export
export default function BlockFaqs({
  content,
  faqs,
  categories,
}: FaqsBlockProps) {
  const {
    topics,
    groups,
    activeCategory,
    topicsRef,
    questionsRef,
    selectTopic,
  } = useFaqs({
    faqs,
    categories,
  })

  // Inactive topics are hidden rather than left out, so every question is in the
  // server rendered HTML. The FAQPage JSON-LD is built by the route
  return (
    <div className="container">
      {content && (
        <div className="mb-10">
          <ContentEditor content={content} />
        </div>
      )}
      <div className="grid grid-cols-12 gap-6 lg:gap-8 xl:gap-10">
        <div
          ref={topicsRef}
          className="col-span-12 md:col-span-4"
        >
          <div className="min-h-[400px] rounded-2xl bg-gray-100 p-6 lg:p-8">
            {topics.length > 0 ? (
              <>
                <Heading
                  tag="h2"
                  size="h5"
                >
                  Topics
                </Heading>
                <ul>
                  {topics.map((e, i) => (
                    <li key={i}>
                      <button
                        onClick={() => selectTopic(e.id)}
                        className={clsx(
                          'flex w-full justify-between py-4 text-left',
                          {
                            'border-b-2 border-teal-green font-semibold text-teal-green':
                              activeCategory == e.id,
                            'border-b border-neutral-200':
                              activeCategory !== e.id,
                          }
                        )}
                      >
                        {e.title}
                        <ChevronRightIcon className="size-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p>No categories found</p>
            )}
          </div>
        </div>
        <div
          ref={questionsRef}
          className="col-span-12 scroll-mt-6 md:col-span-8"
        >
          {groups.length > 0 ? (
            groups.map(group => (
              <div
                key={group.id}
                hidden={activeCategory !== group.id}
              >
                {group.items.length > 0 ? (
                  <Accordion>
                    {group.items.map(e => (
                      <Accordion.Section
                        title={e.question}
                        className="mb-2"
                        key={e._id}
                      >
                        {e.answer && <ContentEditor content={e.answer} />}
                      </Accordion.Section>
                    ))}
                  </Accordion>
                ) : (
                  <p>No faqs found</p>
                )}
              </div>
            ))
          ) : (
            <p>No faqs found</p>
          )}
        </div>
      </div>
    </div>
  )
}
